import { Link } from "react-router-dom";
import { ArrowRight, Phone, MapPin, Check, Warehouse } from "lucide-react";
import Reveal from "@/components/Reveal";
import SEOHead from "@/components/SEOHead";
import FaqSection from "@/components/FaqSection";
import { photoDims } from "@/lib/photos";
import { track } from "@/lib/analytics";

const PAGE_PHOTOS = [
    { src: "/photos/hh-12.webp", alt: "Aerial view of a Harris Homes build on private land in the Tahlequah, OK area" },
    { src: "/photos/hh-20.webp", alt: "Finished ground-up build by Harris Homes in Tahlequah, Oklahoma" },
    { src: "/photos/hh-03.webp", alt: "Custom-built home exterior in Tahlequah, OK by Harris Homes" },
];

const FAQS = [
    {
        q: "Do you build shop houses and barndominiums?",
        a: "Yes. Harris Homes designs and builds shop houses and barndominiums on your land in Tahlequah and across Cherokee County — real workshop or garage space and finished living space under one roof.",
    },
    {
        q: "Steel frame or wood frame — which should I choose?",
        a: "It depends on the span you need, your budget, and the look you're after. We walk your land with you and talk through both options honestly before recommending one.",
    },
    {
        q: "Can a shop house be built before my main home?",
        a: "Many landowners start with the shop house so the land becomes usable right away, then build the main home when they're ready. We plan both with the whole property in mind.",
    },
    {
        q: "How do we get started?",
        a: "Call (918) 373-4341 or send your lot details through the contact form. We'll tell you honestly what fits your land and your budget.",
    },
];

export default function ShopHouseBarndominium() {
    return (
        <div data-testid="shop-house-barndominium-page">
            <SEOHead
                title="Shop House & Barndominium Builder in Tahlequah, OK"
                description="Harris Homes builds shop houses and barndominiums on your land in Tahlequah, OK. Steel or wood frame, living space plus shop. Call 918-373-4341."
                canonical="https://harrishomesok.com/shop-house-barndominium-builder-tahlequah-ok"
            />

            {/* Header */}
            <section className="pt-20 md:pt-32 pb-16 md:pb-20 px-6 md:px-10">
                <div className="max-w-[1480px] mx-auto">
                    <Reveal>
                        <div className="overline mb-6">
                            <MapPin size={11} strokeWidth={1.5} className="inline mr-2 -mt-0.5" style={{ color: "var(--hh-gold)" }} />
                            Tahlequah · Cherokee County
                        </div>
                        <h1 className="font-display text-4xl sm:text-6xl md:text-7xl leading-[1.0] tracking-tight max-w-5xl">
                            Shop House and Barndominium Builder in
                            <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}> Tahlequah, Oklahoma</span>
                        </h1>
                        <p className="mt-10 max-w-2xl text-base md:text-lg leading-relaxed" style={{ color: "var(--hh-text-dim)" }}>
                            We build shop houses and barndominiums on land you already own —
                            serious shop or garage space paired with finished living quarters,
                            planned and built by one accountable local builder. Call{" "}
                            <a href="tel:+19183734341" data-testid="shophouse-intro-phone" onClick={() => track.phoneClick("shophouse-intro")} className="underline underline-offset-4" style={{ color: "var(--hh-gold)" }}>
                                (918) 373-4341
                            </a>{" "}
                            to talk through your lot.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link to="/contact" data-testid="shophouse-hero-cta" className="btn-gold">
                                Get a Quote <ArrowRight size={14} />
                            </Link>
                            <Link to="/services" data-testid="shophouse-services-link" className="btn-ghost">
                                All Services
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* What is it */}
            <section className="py-20 md:py-28 px-6 md:px-10" style={{ background: "var(--hh-bg-2)" }}>
                <div className="max-w-[1480px] mx-auto grid md:grid-cols-12 gap-12 md:gap-16">
                    <Reveal className="md:col-span-6">
                        <div className="overline mb-5">The Basics</div>
                        <h2 className="font-display text-4xl md:text-5xl leading-[1.1] tracking-tight">
                            What is a shop house or barndominium?
                        </h2>
                        <div className="gold-rule w-24 my-8" />
                        <div className="space-y-6 text-base md:text-lg leading-relaxed" style={{ color: "var(--hh-text-dim)" }}>
                            <p>
                                A shop house pairs a full-size workshop, garage, or equipment bay
                                with comfortable living space under a single roof. A barndominium
                                does the same with barn-style construction. Both fit landowners
                                who work from home, keep equipment, run a small operation, or
                                simply want more useful square footage for the money.
                            </p>
                            <p>
                                It's one structure, one foundation, one roofline — and one builder
                                answering for all of it.
                            </p>
                        </div>
                    </Reveal>
                    <div className="md:col-span-6 grid gap-10">
                        <Reveal delay={0.1}>
                            <div data-testid="shophouse-layouts">
                                <h3 className="font-display text-2xl md:text-3xl mb-3 leading-tight flex items-center gap-3">
                                    <Warehouse size={22} strokeWidth={1.2} style={{ color: "var(--hh-gold)" }} />
                                    Living space and shop layouts we build
                                </h3>
                                <p className="text-sm md:text-base leading-relaxed" style={{ color: "var(--hh-text-dim)" }}>
                                    Layouts are drawn around how you'll actually use the building:
                                    bay count and door sizes for your vehicles or equipment, shop
                                    ceiling height, and a living side that can range from a simple
                                    one-bedroom quarters to a full family home.
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={0.18}>
                            <div data-testid="shophouse-frames">
                                <h3 className="font-display text-2xl md:text-3xl mb-3 leading-tight flex items-center gap-3">
                                    <Check size={22} strokeWidth={1.2} style={{ color: "var(--hh-gold)" }} />
                                    Steel frame or wood frame
                                </h3>
                                <p className="text-sm md:text-base leading-relaxed" style={{ color: "var(--hh-text-dim)" }}>
                                    Steel frames span wide open shop space with fewer posts and
                                    shrug off weather; wood frames can cost less on smaller builds
                                    and finish out like a conventional home inside. We'll walk your
                                    land and price both so you can choose with real numbers.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Cost + land */}
            <section className="py-24 md:py-32 px-6 md:px-10">
                <div className="max-w-[1480px] mx-auto grid md:grid-cols-12 gap-12 md:gap-16">
                    <Reveal className="md:col-span-6">
                        <div className="overline mb-5">Budget & Schedule</div>
                        <h2 className="font-display text-4xl md:text-5xl leading-[1.1] tracking-tight">
                            What it costs and
                            <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}> how long it takes.</span>
                        </h2>
                        <div className="gold-rule w-24 my-8" />
                        <div className="space-y-6 text-base md:text-lg leading-relaxed" style={{ color: "var(--hh-text-dim)" }}>
                            <p>
                                There's no honest one-size number for a shop house, so we don't
                                quote one. Price moves with the size and bay count, the frame you
                                choose, how finished the living space is, and what your site
                                needs — driveway, septic or water, and electric.
                            </p>
                            <p>
                                What we promise is an itemized, honest number before anything is
                                built, and a realistic schedule from permit to move-in that we
                                keep you updated on weekly.
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.12} className="md:col-span-6">
                        <div className="overline mb-5">Your Land</div>
                        <h2 className="font-display text-4xl md:text-5xl leading-[1.1] tracking-tight">
                            Building on your land
                            <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}> near Tahlequah.</span>
                        </h2>
                        <div className="gold-rule w-24 my-8" />
                        <p className="text-base md:text-lg leading-relaxed" style={{ color: "var(--hh-text-dim)" }}>
                            Every build starts with a walk of your lot. We look at access and
                            driveway, slope and drainage, septic or water options, and where the
                            building should sit — then handle permits in Cherokee County and
                            full-scope construction from site prep to final finish.
                        </p>
                        <ul className="mt-8 space-y-3 max-w-md">
                            {[
                                "Lot walk and honest read on site costs",
                                "Septic, water, and driveway planning",
                                "Cherokee County permits handled for you",
                                "One builder from first sketch to keys",
                            ].map((item) => (
                                <li key={item} className="flex items-start gap-3 text-sm" style={{ color: "var(--hh-text)" }}>
                                    <Check size={16} strokeWidth={1.6} style={{ color: "var(--hh-gold)", marginTop: 2 }} />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                </div>
            </section>

            {/* Photos */}
            <section className="py-20 md:py-28 px-6 md:px-10" style={{ background: "var(--hh-bg-2)" }}>
                <div className="max-w-[1480px] mx-auto">
                    <Reveal>
                        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                            <div>
                                <div className="overline mb-5">Our Work</div>
                                <h2 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-tight max-w-2xl">
                                    Recent builds around Tahlequah.
                                </h2>
                            </div>
                            <Link to="/projects" data-testid="shophouse-projects-link" className="btn-ghost self-start">
                                Full Gallery <ArrowRight size={14} />
                            </Link>
                        </div>
                    </Reveal>
                    <div className="grid md:grid-cols-3 gap-3 md:gap-4">
                        {PAGE_PHOTOS.map((p, i) => {
                            const d = photoDims(p.src);
                            return (
                                <Reveal key={p.src} delay={i * 0.08}>
                                    <div className="relative img-zoom h-[280px] md:h-[340px]">
                                        <img
                                            src={p.src}
                                            alt={p.alt}
                                            width={d?.w}
                                            height={d?.h}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <FaqSection
                overline="Frequently Asked"
                headingLead="Questions people ask"
                headingItalic="about shop houses."
                items={FAQS}
                testId="shophouse-faq"
            />

            {/* CTA */}
            <section className="py-28 md:py-40 px-6 md:px-10 text-center">
                <Reveal>
                    <div className="overline mb-5">Get a Quote</div>
                    <h2 className="font-display text-4xl md:text-6xl leading-tight tracking-tight max-w-3xl mx-auto">
                        Tell us about your land and
                        <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}> what you need to park in it.</span>
                    </h2>
                    <div className="mt-10 flex flex-wrap gap-4 justify-center">
                        <Link to="/contact" data-testid="shophouse-final-cta" onClick={() => track.ctaClick("Get a Quote", "shophouse-final")} className="btn-gold">
                            Get a Quote <ArrowRight size={14} />
                        </Link>
                        <a href="tel:+19183734341" data-testid="shophouse-final-phone" onClick={() => track.phoneClick("shophouse-final")} className="btn-ghost">
                            <Phone size={14} strokeWidth={1.5} /> (918) 373-4341
                        </a>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
